import React, { useEffect, useState, useRef } from "react";
import {
  Loader2,
  Calendar,
  Award,
  ArrowRight,
  Film,
  ChevronDown,
} from "lucide-react";
import "./Home.css";

import fetchFromAPI from "../utils/fetchFromAPI";
import { formatDeadline } from "../utils/deadline";
import HowItWorks from "../components/HowItWorks";

const ChallengeCardSkeleton = () => (
  <div className="challenge-card revealed">
    {/* Eyebrow */}
    <div className="skeleton skeleton-eyebrow" />

    {/* Rules grid */}
    <div className="challenge-rules-grid">
      <div className="challenge-rule-left">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-rule" />
      </div>
      <div
        className="challenge-rule-divider"
        style={{ transform: "scaleY(1)" }}
      />
      <div className="challenge-rule-right">
        <div className="skeleton skeleton-label" />
        <div className="skeleton skeleton-rule skeleton-rule-wide" />
      </div>
    </div>

    {/* Footer */}
    <div className="challenge-footer" style={{ opacity: 1, transform: "none" }}>
      <div className="skeleton skeleton-deadline" />
      <div className="skeleton skeleton-button" />
    </div>
  </div>
);

const Home = () => {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await fetchFromAPI("/api/rules");
        setRules(response.data);
      } catch (error) {
        console.error("Error fetching rules:", error);
        setRules(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  // Intersection observer for fade-in + challenge card typewriter
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.05 },
    );

    const cardObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
          cardObserver.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    const elements = document.querySelectorAll(".fade-in");
    elements.forEach((el) => observer.observe(el));

    // Only observe the challenge card once it has real content (not skeleton)
    if (!loading) {
      const card = document.getElementById("challenge-card");
      if (card) cardObserver.observe(card);
    }

    return () => {
      observer.disconnect();
      cardObserver.disconnect();
    };
  }, [loading]);

  return (
    <div className="home-container content-visible">
      {/* Video Hero - full width */}
      <section className="video-hero">
        <video className="video-hero-bg" autoPlay muted loop playsInline>
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="video-hero-overlay" />
        <div className="video-hero-content">
          <p className="video-hero-eyebrow">SHORT CUTS NYC</p>
          <h1 className="video-hero-slogan">Democratize Filmmaking</h1>
        </div>
        <button
          className="video-hero-scroll"
          aria-label="Scroll down"
          onClick={() => {
            document
              .getElementById("intro")
              .scrollIntoView({ behavior: "smooth" });
          }}
        >
          <ChevronDown size={28} className="scroll-arrow" />
        </button>
      </section>
      {/* Intro Strip */}
      <section className="intro-strip fade-in" id="intro">
        <div className="intro-strip-inner">
          <p className="intro-text">
            Every month, we draw two rules.{" "}
            <em>You make a film and we screen it for you.</em>
          </p>
          <p className="intro-subtext">
            Short Cuts NYC is a filmmaking collective that breaks down the
            barriers standing between people and the films they want to make.{" "}
            <a href="/about" className="intro-learn-more">
              Learn More →
            </a>
          </p>
        </div>
      </section>
      <div className="home-content">
        {/* Divider */}
        <div className="section-divider" />

        {/* How It Works */}
        <HowItWorks />

        {/* Challenge Section */}
        <section className="challenge-section">
          {loading ? (
            <ChallengeCardSkeleton />
          ) : !rules || Object.keys(rules).length === 0 ? (
            <div className="challenge-card" id="challenge-card">
              <div className="rules-unavailable">
                <Film size={40} color="#eb8876" />
                <h2>Challenge Rules Unavailable</h2>
                <p>
                  The current challenge's rules aren't available at the moment.
                  Please check back later.
                </p>
              </div>
            </div>
          ) : (
            <div className="challenge-card" id="challenge-card">
              <p className="challenge-eyebrow">
                Season {rules.season} · Challenge {rules.challenge}
              </p>

              <div className="challenge-rules-grid">
                <div className="challenge-rule-left">
                  <div className="challenge-rule-label">FORM</div>
                  <div className="challenge-rule-text">{rules.form}</div>
                </div>
                <div className="challenge-rule-divider" />
                <div className="challenge-rule-right">
                  <div className="challenge-rule-label">WILDCARD</div>
                  <div className="challenge-rule-text">{rules.wildcard}</div>
                </div>
              </div>

              <div className="challenge-footer">
                <div className="challenge-deadline">
                  <Calendar size={16} className="deadline-icon" />
                  <span>Deadline: </span>
                  <span className="deadline-date">
                    {formatDeadline(rules.due_date) ?? "Not available"}
                  </span>
                </div>
                <a href="/submit" className="submit-button">
                  <span>Submit Your Film</span>
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          )}
        </section>

        {/* Image Banner */}
        <section className="banner-section fade-in">
          <img
            src="/vino.jpg"
            alt="Short Cuts NYC Community"
            className="banner-image"
          />
          <div className="banner-overlay">
            <h2>Create. Share. Learn.</h2>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="section-header">
            <h2>Our Values</h2>
          </div>
          {/* <div className="values-grid">
            <div className="scene-card fade-in delay-1">
              <div className="scene-pin" />
              <h3 className="scene-card-title">Community of Creatives</h3>
              <p className="scene-card-desc">
                We believe in the power of collaboration and the strength of a
                creative community. Our goal is to bring together talented
                individuals who can support and uplift each other in their
                artistic pursuits.
              </p>
            </div>

            <div className="scene-card fade-in delay-2">
              <div className="scene-pin" />
              <h3 className="scene-card-title">Inclusion and Respect</h3>
              <p className="scene-card-desc">
                Our filmmaking community values inclusion and respect for all
                members, regardless of their background or experience. We strive
                to create a welcoming environment where everyone feels valued
                and respected.
              </p>
            </div>

            <div className="scene-card fade-in delay-3">
              <div className="scene-pin" />
              <h3 className="scene-card-title">Learning Environment</h3>
              <p className="scene-card-desc">
                Short Cuts is all about learning by doing, where we believe
                everyone has valuable knowledge to share and new skills to
                acquire. This is the perfect place to embrace experimentation
                and make mistakes, fostering growth through hands-on, on set
                experience and mutual support.
              </p>
            </div>

            <div className="scene-card fade-in delay-4">
              <div className="scene-pin" />
              <h3 className="scene-card-title">
                Collaboration and Experimentation
              </h3>
              <p className="scene-card-desc">
                Our community provides a space where members can come together
                to share ideas, try new approaches, and push the boundaries of
                what's possible. We tackle the unknown through collaboration.
              </p>
            </div>

            <div className="scene-card fade-in delay-5">
              <div className="scene-pin" />
              <h3 className="scene-card-title">Everyone is Equal</h3>
              <p className="scene-card-desc">
                We believe that everyone has something valuable to contribute
                and should be treated with equal respect and consideration
                regardless of socioeconomic status, race, religion, and other
                factors.
              </p>
            </div>

            <div className="scene-card fade-in delay-6">
              <div className="scene-pin" />
              <h3 className="scene-card-title">Perfection through Practice</h3>
              <p className="scene-card-desc">
                We believe that the key to success in filmmaking is through
                practice and perseverance. We strive for excellence through
                getting our reps in and never stop learning and improving.
              </p>
            </div>
          </div> */}

          <div className="values-grid">
            <div className="value-item">
              <h3 className="value-title">Community of Creatives</h3>
              <p className="value-body">
                We believe in the power of collaboration and the strength of a
                creative community. Our goal is to bring together talented
                individuals who can support and uplift each other in their
                artistic pursuits.
              </p>
            </div>
            <div className="value-item">
              <h3 className="value-title">Inclusion and Respect</h3>
              <p className="value-body">
                Our filmmaking community values inclusion and respect for all
                members, regardless of their background or experience. We strive
                to create a welcoming environment where everyone feels valued
                and respected.
              </p>
            </div>
            <div className="value-item">
              <h3 className="value-title">Learning Environment</h3>
              <p className="value-body">
                Short Cuts is all about learning by doing. This is the perfect
                place to embrace experimentation, make mistakes, and grow
                through hands-on, on set experience and mutual support.
              </p>
            </div>
            <div className="value-item">
              <h3 className="value-title">Collaboration and Experimentation</h3>
              <p className="value-body">
                Our community provides a space to share ideas, try new
                approaches, and push the boundaries of what's possible. We
                tackle the unknown through collaboration.
              </p>
            </div>
            <div className="value-item">
              <h3 className="value-title">Everyone is Equal</h3>
              <p className="value-body">
                Everyone has something valuable to contribute and should be
                treated with equal respect regardless of socioeconomic status,
                race, religion, and other factors.
              </p>
            </div>
            <div className="value-item">
              <h3 className="value-title">Perfection through Practice</h3>
              <p className="value-body">
                The key to success in filmmaking is practice and perseverance.
                We strive for excellence by getting our reps in and never
                stopping learning.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section fade-in">
          <div className="cta-left">
            <p className="cta-eyebrow">NEXT STEPS</p>
            <h2 className="cta-heading">Ready to Make Something?</h2>
            <p className="cta-body">
              Short Cuts meets every month. No budget needed, no experience
              required. Just show up. We draw the rules, you make the film.
            </p>
          </div>

          <div className="cta-divider" />

          <div className="cta-right">
            <a
              href="https://www.meetup.com/short-cuts/"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-action"
            >
              <div className="cta-action-icon">
                <img src="/meetup.svg" alt="Meetup" />
              </div>
              <div>
                <span className="cta-action-label">Join on Meetup</span>
                <span className="cta-action-sub">
                  RSVP for the next screening
                </span>
              </div>
            </a>

            <a href="/submit" className="cta-action">
              <div className="cta-action-icon">
                <svg
                  viewBox="-2 -2 44 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="20"
                    cy="20"
                    r="20"
                    fill="white"
                    fillOpacity="0.15"
                    stroke="white"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="11"
                    y="14"
                    width="18"
                    height="13"
                    rx="2"
                    stroke="white"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <path
                    d="M17 14v-2a1 1 0 011-1h4a1 1 0 011 1v2"
                    stroke="white"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M20 18v5M17.5 20.5L20 18l2.5 2.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <span className="cta-action-label">Submit Your Film</span>
                <span className="cta-action-sub">
                  Before the challenge deadline
                </span>
              </div>
            </a>

            <a
              href="https://discord.gg/b9KRsFd2bQ"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-action"
            >
              <div className="cta-action-icon">
                <img src="/discord.svg" alt="Discord" />
              </div>
              <div>
                <span className="cta-action-label">Join our Discord</span>
                <span className="cta-action-sub">Chat with the community</span>
              </div>
            </a>
          </div>
        </section>
      </div>{" "}
      {/* end home-content */}
    </div>
  );
};

export default Home;
